import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  BarChart3,
  Users,
  Award,
  TrendingUp,
  Clock,
  Target,
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  AlertCircle,
  Keyboard,
  GraduationCap,
  Activity
} from 'lucide-react';

const RPATAdminAnalytics = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [phaseBreakdown, setPhaseBreakdown] = useState(null);
  const [topicStats, setTopicStats] = useState(null);
  const [assessmentStats, setAssessmentStats] = useState(null);
  const [typingStats, setTypingStats] = useState(null);
  const [dropOff, setDropOff] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      
      const [overviewRes, phaseRes, topicRes, assessmentRes, typingRes, dropOffRes] = await Promise.all([
        api.get('/rpat/admin/analytics/overview'),
        api.get('/rpat/admin/analytics/phase-breakdown'),
        api.get('/rpat/admin/analytics/topic-completion'),
        api.get('/rpat/admin/analytics/assessment-performance'),
        api.get('/rpat/admin/analytics/typing-test-performance'),
        api.get('/rpat/admin/analytics/drop-off-analysis')
      ]);
      
      setOverview(overviewRes.data);
      setPhaseBreakdown(phaseRes.data);
      setTopicStats(topicRes.data);
      setAssessmentStats(assessmentRes.data);
      setTypingStats(typingRes.data);
      setDropOff(dropOffRes.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-32" />)}
          </div>
          <Skeleton className="h-96" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/rpat')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">R-PAT Analytics</h1>
            <p className="text-muted-foreground">Training program performance insights</p>
          </div>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Enrollments</p>
                  <p className="text-3xl font-bold text-cyan-700">{overview?.enrollments?.total || 0}</p>
                </div>
                <Users className="h-10 w-10 text-cyan-600" />
              </div>
              <p className="text-xs text-cyan-600 mt-2">
                {overview?.enrollments?.active_last_7_days || 0} active this week
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Certified</p>
                  <p className="text-3xl font-bold text-emerald-700">{overview?.enrollments?.certified || 0}</p>
                </div>
                <Award className="h-10 w-10 text-emerald-600" />
              </div>
              <p className="text-xs text-emerald-600 mt-2">
                {overview?.certificates?.total_issued || 0} certificates issued
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Completion Rate</p>
                  <p className="text-3xl font-bold text-violet-700">{overview?.completion_rate || 0}%</p>
                </div>
                <TrendingUp className="h-10 w-10 text-violet-600" />
              </div>
              <Progress value={overview?.completion_rate || 0} className="mt-2 h-2" />
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg Completion</p>
                  <p className="text-3xl font-bold text-amber-700">{overview?.performance?.avg_completion_hours || 0}h</p>
                </div>
                <Clock className="h-10 w-10 text-amber-600" />
              </div>
              <p className="text-xs text-amber-600 mt-2">
                Target: 53 hours
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Analytics Tabs */}
        <Tabs defaultValue="phases" className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="phases">Phases</TabsTrigger>
            <TabsTrigger value="topics">Topics</TabsTrigger>
            <TabsTrigger value="assessments">Assessments</TabsTrigger>
            <TabsTrigger value="typing">Typing</TabsTrigger>
            <TabsTrigger value="dropoff">Drop-off</TabsTrigger>
          </TabsList>

          {/* Phase Breakdown */}
          <TabsContent value="phases">
            <Card>
              <CardHeader>
                <CardTitle>Phase Completion Breakdown</CardTitle>
                <CardDescription>User progress through each phase</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {phaseBreakdown?.phases?.map((phase, index) => (
                    <div key={phase.phase_id} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                            index === 0 ? 'bg-cyan-600' :
                            index === 1 ? 'bg-violet-600' :
                            index === 2 ? 'bg-amber-600' : 'bg-emerald-600'
                          }`}>
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium">{phase.phase_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {phase.topic_count} topics
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">{phase.completion_rate}%</p>
                          <p className="text-sm text-muted-foreground">
                            {phase.users_completed} / {phase.users_started} users
                          </p>
                        </div>
                      </div>
                      <Progress value={phase.completion_rate} className="h-2" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Topic Stats */}
          <TabsContent value="topics">
            <Card>
              <CardHeader>
                <CardTitle>Topic Completion Statistics</CardTitle>
                <CardDescription>Detailed breakdown by topic</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-96">
                  <div className="space-y-2">
                    {topicStats?.topics?.map((topic) => (
                      <div 
                        key={topic.topic_id}
                        className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50"
                      >
                        <div className="flex items-center gap-3">
                          <Badge variant="outline">{topic.topic_code}</Badge>
                          <span className="font-medium truncate max-w-xs">{topic.topic_title}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <div className="text-center">
                            <p className="font-bold text-emerald-600">{topic.completed_count}</p>
                            <p className="text-xs text-muted-foreground">Completed</p>
                          </div>
                          <div className="text-center">
                            <p className="font-bold text-amber-600">{topic.in_progress_count}</p>
                            <p className="text-xs text-muted-foreground">In Progress</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Assessment Stats */}
          <TabsContent value="assessments">
            <Card>
              <CardHeader>
                <CardTitle>Assessment Performance</CardTitle>
                <CardDescription>Pass rates and average scores</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <Card className="bg-emerald-50 border-emerald-200">
                    <CardContent className="pt-4 text-center">
                      <p className="text-3xl font-bold text-emerald-700">
                        {overview?.performance?.assessment_pass_rate || 0}%
                      </p>
                      <p className="text-sm text-emerald-600">Overall Pass Rate</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-cyan-50 border-cyan-200">
                    <CardContent className="pt-4 text-center">
                      <p className="text-3xl font-bold text-cyan-700">80%</p>
                      <p className="text-sm text-cyan-600">Required to Pass</p>
                    </CardContent>
                  </Card>
                </div>
                
                <ScrollArea className="h-64">
                  <div className="space-y-2">
                    {assessmentStats?.assessments?.map((assessment) => (
                      <div 
                        key={assessment.assessment_id}
                        className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{assessment.title}</p>
                          <p className="text-sm text-muted-foreground">
                            {assessment.total_attempts} attempts
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <p className="font-bold">{assessment.avg_score}%</p>
                            <p className="text-xs text-muted-foreground">Avg Score</p>
                          </div>
                          <Badge variant={assessment.pass_rate >= 70 ? 'default' : 'destructive'}>
                            {assessment.pass_rate}% pass
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Typing Stats */}
          <TabsContent value="typing">
            <Card>
              <CardHeader>
                <CardTitle>Typing Test Performance</CardTitle>
                <CardDescription>Speed and accuracy metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <Card className="bg-violet-50 border-violet-200">
                    <CardContent className="pt-4 text-center">
                      <p className="text-3xl font-bold text-violet-700">
                        {overview?.performance?.typing_test_pass_rate || 0}%
                      </p>
                      <p className="text-sm text-violet-600">Overall Pass Rate</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-cyan-50 border-cyan-200">
                    <CardContent className="pt-4 text-center">
                      <p className="text-3xl font-bold text-cyan-700">10</p>
                      <p className="text-sm text-cyan-600">Total Tests</p>
                    </CardContent>
                  </Card>
                </div>
                
                <ScrollArea className="h-64">
                  <div className="space-y-2">
                    {typingStats?.typing_tests?.map((test) => (
                      <div 
                        key={test.test_number}
                        className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
                            <span className="font-bold text-violet-700">{test.test_number}</span>
                          </div>
                          <div>
                            <p className="font-medium">{test.title}</p>
                            <p className="text-xs text-muted-foreground">
                              Min: {test.min_wpm_required} WPM, {test.min_accuracy_required}% accuracy
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <p className="font-bold text-cyan-600">{test.avg_wpm}</p>
                            <p className="text-xs text-muted-foreground">Avg WPM</p>
                          </div>
                          <div className="text-center">
                            <p className="font-bold text-emerald-600">{test.avg_accuracy}%</p>
                            <p className="text-xs text-muted-foreground">Avg Accuracy</p>
                          </div>
                          <Badge variant={test.pass_rate >= 70 ? 'default' : 'secondary'}>
                            {test.pass_rate}%
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Drop-off Analysis */}
          <TabsContent value="dropoff">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                  Drop-off Analysis
                </CardTitle>
                <CardDescription>Where users are stopping in the course</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <p className="text-amber-800">
                    <strong>{dropOff?.total_incomplete_users || 0}</strong> users have not completed the course
                  </p>
                </div>
                
                <div className="space-y-3">
                  {dropOff?.drop_off_points?.map((point, index) => (
                    <div 
                      key={`dropoff-${point.topic?.substring(0, 20) || index}`}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          index === 0 ? 'bg-red-100 text-red-700' :
                          index === 1 ? 'bg-amber-100 text-amber-700' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          {index + 1}
                        </div>
                        <span className="font-medium">{point.topic}</span>
                      </div>
                      <Badge variant={index === 0 ? 'destructive' : 'secondary'}>
                        {point.count} users
                      </Badge>
                    </div>
                  ))}
                </div>
                
                {(!dropOff?.drop_off_points || dropOff.drop_off_points.length === 0) && (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-emerald-600" />
                    <p>No significant drop-off points detected</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default RPATAdminAnalytics;
